// Tab de Mandato de Representación para el detalle de contrato admin
// Un código de mandato por cada persona que contrata; si hay 2 contratantes/intervinientes, 2 mandatos separados.
// Soporta: individual (1 mandato), duo/family (2 mandatos por holder), linked/2x1 (cada contrato tiene su mandato).

import { useState, useCallback, useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { SignaturePad } from '@/components/mandate/SignaturePad';
import { mandateService } from '@/services/mandateService';
import { Skeleton } from '@/components/common/Skeleton';
import {
  ScaleIcon,
  ArrowDownTrayIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  UserIcon,
  LinkIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'sonner';
import type { Contract } from '@/types/contracts';
import { config } from '@/config/constants';

interface ContractMandateTabProps {
  contract: Contract;
  onContractUpdate: () => void;
  /** Navegar al detalle de otro contrato (p.ej. linked). */
  onNavigateToContract?: (code: string) => void;
}

const MANDATE_STATUS_CONFIG = {
  signed: {
    color: 'bg-green-100 text-green-800 border-green-200',
    icon: CheckCircleIcon,
    label: 'Firmado',
  },
  pending: {
    color: 'bg-orange-100 text-orange-800 border-orange-200',
    icon: ClockIcon,
    label: 'Pendiente',
  },
  revoked: {
    color: 'bg-red-100 text-red-800 border-red-200',
    icon: XCircleIcon,
    label: 'Revocado',
  },
  none: {
    color: 'bg-gray-100 text-gray-600 border-gray-200',
    icon: ScaleIcon,
    label: 'Sin mandato',
  },
} as const;

// Texto de preview del mandato (texto legal oficial)
const MANDATE_PREVIEW_TEXT = `En Salamanca, a ___ de _________ de _________

REUNIDOS

De una parte, D. _______________________, mayor de edad, con domicilio a efectos de notificaciones en _________________________________________________, con Pasaporte nº ___________________ y/o en su caso NIE _____________, móvil _______________ y mail _____________________, en su propio nombre, en adelante MANDANTE.

De otra parte, la entidad MIGRO SERVICIOS Y REMESAS SL, con CIF B22759765, con domicilio social en C/ Libreros 4, 1º de Salamanca – España, debidamente representada en función de la escritura de constitución social de fecha 15 de julio de 2025 y protocolo 940/25 otorgada ante el Notario de Huelva, Dª María Gómez – Rodulfo García de Castro, en lo sucesivo denominada entidad MANDATARIA.

EXPONEN:

Que el MANDANTE confiere a través del presente documento, con carácter general, MANDATO CON REPRESENTACIÓN a favor de la MANDATARIA, esto es la entidad MIGRO SERVICIOS Y REMESAS SL, para que promueva/n, solicite/n y realice/n todos los trámites necesarios en materia de extranjería, inmigración y residencia ante los órganos y entidades de la Administración del Estado, Autonómica, Provincial y Local que resulten competentes en dichas materias, sin ningún tipo de limitación o exclusión en el ámbito de las mismas.

El presente mandato se rige por los arts.1709 a 1739 del Código Civil, así como al amparo del art.5 de la Ley 39/2015, de 1 de octubre, del Procedimiento Administrativo Común de las Administraciones Públicas. [...] (texto completo en el PDF generado).`;

/** Construye la lista de intervinientes (titulares) del contrato para mostrar un mandato por persona. */
function getIntervinientes(contract: Contract): Array<{ name: string; email: string; isPrimary: boolean; index: number }> {
  const holders = contract.holders;
  if (holders && holders.length >= 1) {
    const sorted = [...holders].sort((a, b) => (a.is_primary === true ? -1 : 1) - (b.is_primary === true ? -1 : 1));
    return sorted.map((h, i) => ({
      name: h.full_name || contract.client_name || '—',
      email: h.email || contract.client_email || '—',
      isPrimary: !!h.is_primary,
      index: i,
    }));
  }
  return [
    {
      name: contract.client_name || 'Titular',
      email: contract.client_email || '—',
      isPrimary: true,
      index: 0,
    },
  ];
}

/** Obtiene el código de mandato para un interviniente (por índice). Backend puede devolver mandate_codes[] o mandates[]. */
function getMandateCodeForHolder(
  contract: Contract,
  holderIndex: number
): string | null {
  if (contract.mandate_codes && contract.mandate_codes[holderIndex] != null) {
    return contract.mandate_codes[holderIndex];
  }
  if (contract.mandates && contract.mandates[holderIndex]) {
    return contract.mandates[holderIndex].mandate_code;
  }
  return null;
}

/** Estado y datos de mandato para un titular. Por ahora el backend tiene un solo mandato por contrato (primer titular). */
function getMandateStateForHolder(
  contract: Contract,
  holderIndex: number
): { status: 'pending' | 'signed' | 'revoked' | null; pdfUrl: string | null; signedAt: string | null } {
  const byHolder = contract.mandates?.[holderIndex];
  if (byHolder) {
    return {
      status: byHolder.mandate_status ?? null,
      pdfUrl: byHolder.mandate_pdf_url ?? null,
      signedAt: byHolder.mandate_signed_at ?? null,
    };
  }
  if (holderIndex === 0) {
    return {
      status: contract.mandate_status ?? null,
      pdfUrl: contract.mandate_pdf_url ?? null,
      signedAt: contract.mandate_signed_at ?? null,
    };
  }
  return { status: null, pdfUrl: null, signedAt: null };
}

export function ContractMandateTab({ contract, onContractUpdate, onNavigateToContract }: ContractMandateTabProps) {
  const [signatureBase64, setSignatureBase64] = useState<string | null>(null);
  const [signingHolderIndex, setSigningHolderIndex] = useState<number>(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isRevoking, setIsRevoking] = useState(false);
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
  const [revokeHolderIndex, setRevokeHolderIndex] = useState<number>(0);

  const intervinientes = useMemo(() => getIntervinientes(contract), [contract]);
  const isMultiHolder = intervinientes.length > 1;
  const isLinked = contract.contract_type === 'linked' && (contract.linked_contract_code || contract.linked_partner_name);

  const handleSignatureChange = useCallback((base64: string | null) => {
    setSignatureBase64(base64);
  }, []);

  const handleGenerateMandate = async (holderIndex: number) => {
    if (!signatureBase64) {
      toast.error('Debe dibujar la firma antes de generar el mandato.');
      return;
    }
    if (signatureBase64.length < 2000) {
      toast.error('La firma es demasiado corta. Dibuje un trazo más amplio.');
      return;
    }

    const code = getMandateCodeForHolder(contract, holderIndex) || contract.hiring_code;
    setIsGenerating(true);
    try {
      const result = await mandateService.generateMandate(code, signatureBase64);
      toast.success('Mandato generado y firmado exitosamente');
      if (result.mandate_pdf_url) {
        toast.info('El PDF del mandato está disponible para descarga.');
      }
      onContractUpdate();
    } catch (error: unknown) {
      console.error('Error generando mandato:', error);
      const err = error as { response?: { data?: { detail?: string }; status?: number }; message?: string };
      if (err?.response?.status === 404) {
        toast.error('Contrato no encontrado.');
      } else if (err?.response?.status === 400) {
        toast.error(err?.response?.data?.detail || 'El mandato ya está firmado o los datos son inválidos.');
      } else {
        toast.error(err?.response?.data?.detail || 'Error al generar el mandato. Inténtelo de nuevo.');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRevokeMandate = async (holderIndex: number) => {
    const code = getMandateCodeForHolder(contract, holderIndex) || contract.hiring_code;
    setIsRevoking(true);
    try {
      await mandateService.revokeMandate(code);
      toast.success('Mandato revocado exitosamente');
      setShowRevokeConfirm(false);
      onContractUpdate();
    } catch (error: unknown) {
      console.error('Error revocando mandato:', error);
      const err = error as { response?: { data?: { detail?: string } }; message?: string };
      toast.error(err?.response?.data?.detail || 'Error al revocar el mandato.');
    } finally {
      setIsRevoking(false);
    }
  };

  const handleDownloadMandate = async (holderIndex: number) => {
    const state = getMandateStateForHolder(contract, holderIndex);
    const mandateCode = getMandateCodeForHolder(contract, holderIndex);
    if (state.pdfUrl) {
      window.open(state.pdfUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    try {
      const blob = await mandateService.downloadMandateCRM(contract.hiring_code, {
        mandate_code: mandateCode || undefined,
        holder_index: mandateCode ? undefined : holderIndex,
      });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (err: unknown) {
      const e = err as { message?: string; status?: number };
      if (e?.status === 404 || (typeof e?.message === 'string' && e.message.includes('no disponible'))) {
        toast.error('Mandato firmado no disponible.');
      } else {
        toast.error('No se pudo descargar el mandato.');
      }
    }
  };

  const openMandateLink = (holderIndex: number) => {
    const code = getMandateCodeForHolder(contract, holderIndex) || contract.hiring_code;
    const url = `${window.location.origin}/mandato/${code}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const copyMandateLink = (holderIndex: number) => {
    const code = getMandateCodeForHolder(contract, holderIndex) || contract.hiring_code;
    const url = `${config.SHORT_URL_BASE || window.location.origin}/mandato/${code}`;
    navigator.clipboard.writeText(url);
    toast.success('Enlace de mandato copiado al portapapeles');
  };

  const formatMandateDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('es-ES', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="space-y-4">
      {/* Contrato vinculado (2x1): aviso y enlace al otro contrato */}
      {isLinked && (
        <Card className="border-amber-200 bg-amber-50/50">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <LinkIcon className="w-4 h-4" />
              Contrato vinculado (2x1) — Otro mandato
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-sm text-gray-700">
              En ofertas 2x1 cada persona tiene su propio contrato y su propio mandato. Este es el mandato del titular de <strong>{contract.client_name}</strong>.
            </p>
            {contract.linked_partner_name && (
              <p className="text-sm text-gray-600">
                Segundo titular de la oferta: <strong>{contract.linked_partner_name}</strong>
              </p>
            )}
            {contract.linked_contract_code && onNavigateToContract && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-2 border-amber-300 text-amber-800 hover:bg-amber-100"
                onClick={() => onNavigateToContract(contract.linked_contract_code!)}
              >
                <LinkIcon className="w-4 h-4" />
                Ver contrato y mandato de {contract.linked_partner_name || contract.linked_contract_code}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Resumen: un mandato por persona */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ScaleIcon className="w-5 h-5" />
            Mandatos de representación
            {isMultiHolder && (
              <Badge variant="outline" className="font-normal">
                {intervinientes.length} titulares
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-600 mb-4">
            Cada persona que contrata debe disponer de su propio mandato con un código único. Si hay dos contratantes intervinientes, se generan dos mandatos separados.
          </p>

          <div className="space-y-6">
            {intervinientes.map((titular, idx) => {
              const mandateCode = getMandateCodeForHolder(contract, idx);
              const state = getMandateStateForHolder(contract, idx);
              const statusKey = state.status || 'none';
              const statusConfig = MANDATE_STATUS_CONFIG[statusKey as keyof typeof MANDATE_STATUS_CONFIG] || MANDATE_STATUS_CONFIG.none;
              const StatusIcon = statusConfig.icon;
              const isFirst = idx === 0;
              const canUseContractMandate = isFirst || !!contract.mandates?.[idx]; // Backend actual: solo primer titular tiene mandato a nivel contrato

              return (
                <Card key={idx} className="border-gray-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <UserIcon className="w-4 h-4" />
                      {isMultiHolder ? `Titular ${idx + 1}` : 'Mandato de representación'}
                      {titular.isPrimary && (
                        <Badge variant="secondary" className="text-xs font-normal">Principal</Badge>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{titular.name}</p>
                      <p className="text-sm text-gray-500">{titular.email}</p>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-gray-500">Código de mandato:</span>
                      <code className="text-sm bg-gray-100 px-2 py-1 rounded font-mono">{mandateCode ?? contract.hiring_code}</code>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8 gap-1"
                        onClick={() => copyMandateLink(idx)}
                      >
                        <DocumentDuplicateIcon className="w-3.5 h-3.5" />
                        Copiar enlace
                      </Button>
                    </div>

                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-gray-500">Estado:</span>
                      <Badge className={`${statusConfig.color} flex items-center gap-1.5`}>
                        <StatusIcon className="w-3.5 h-3.5" />
                        {statusConfig.label}
                      </Badge>
                    </div>

                    {state.status === 'signed' && state.signedAt && (
                      <div>
                        <span className="text-sm font-medium text-gray-500">Fecha de firma:</span>
                        <p className="text-sm text-gray-900 mt-0.5">{formatMandateDate(state.signedAt)}</p>
                      </div>
                    )}

                    {state.status === 'signed' && (
                      <div className="flex flex-col sm:flex-row gap-2">
                        <Button
                          onClick={() => handleDownloadMandate(idx)}
                          variant="default"
                          className="bg-green-600 hover:bg-green-700 text-white gap-2 w-full sm:w-auto min-h-[44px]"
                        >
                          <ArrowDownTrayIcon className="w-4 h-4" />
                          Descargar mandato {mandateCode ?? contract.hiring_code}
                        </Button>
                        <Button
                          onClick={() => {
                            setRevokeHolderIndex(idx);
                            setShowRevokeConfirm(true);
                          }}
                          variant="outline"
                          className="text-red-600 border-red-300 hover:bg-red-50 gap-2 w-full sm:w-auto min-h-[44px]"
                        >
                          <ExclamationTriangleIcon className="w-4 h-4" />
                          Revocar mandato
                        </Button>
                      </div>
                    )}

                    {state.status === 'revoked' && (
                      <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                        <p className="text-sm text-red-800">
                          Este mandato fue revocado. Si necesita un nuevo mandato, el titular debe firmarlo de nuevo desde la página de contratación.
                        </p>
                      </div>
                    )}

                    {!canUseContractMandate && isMultiHolder && (
                      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                        <p className="text-sm text-amber-800">
                          El sistema generará un código único de mandato para este titular cuando el backend lo soporte. Mientras tanto, puede usar el enlace de firma con el código del contrato para este titular.
                        </p>
                      </div>
                    )}

                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="gap-2"
                      onClick={() => openMandateLink(idx)}
                    >
                      Abrir página de firma del mandato
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Sección de firma: solo para el primer titular o cuando no hay mandatos por holder */}
      {intervinientes.length >= 1 && (
        (() => {
          const state0 = getMandateStateForHolder(contract, 0);
          const showSignSection = !state0.status || state0.status === 'pending' || state0.status === 'revoked';
          if (!showSignSection) return null;

          return (
            <>
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Texto del mandato</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 max-h-64 overflow-y-auto">
                    <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">
                      {MANDATE_PREVIEW_TEXT.replace(
                        'D. _______________________',
                        `D. ${intervinientes[0]?.name || contract.client_name || '[nombre del cliente]'}`
                      )}
                    </pre>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">
                    {isMultiHolder ? `Firma del mandante — ${intervinientes[signingHolderIndex]?.name}` : 'Firma del mandante'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {isMultiHolder && (
                    <div className="flex flex-wrap gap-2">
                      {intervinientes.map((t, i) => (
                        <Button
                          key={i}
                          type="button"
                          variant={signingHolderIndex === i ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setSigningHolderIndex(i)}
                        >
                          {t.name}
                        </Button>
                      ))}
                    </div>
                  )}
                  <p className="text-sm text-gray-500">
                    Firme con el ratón, dedo o stylus. La firma se embebe en el PDF del mandato.
                  </p>

                  <SignaturePad
                    onSignatureChange={handleSignatureChange}
                    height={200}
                    hintText="Firme aquí con el ratón o dedo"
                    disabled={isGenerating}
                  />

                  <Button
                    onClick={() => handleGenerateMandate(signingHolderIndex)}
                    disabled={!signatureBase64 || isGenerating}
                    className="w-full bg-green-600 hover:bg-green-700 text-white min-h-[48px] text-base font-medium"
                  >
                    {isGenerating ? (
                      <span className="flex items-center gap-2">
                        <Skeleton className="w-5 h-5 rounded-full" variant="circular" />
                        Generando mandato...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <CheckCircleIcon className="w-5 h-5" />
                        Firmar y generar mandato {isMultiHolder ? `(${intervinientes[signingHolderIndex]?.name})` : ''}
                      </span>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </>
          );
        })()
      )}

      {/* Modal de confirmación de revocación */}
      {showRevokeConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <ExclamationTriangleIcon className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Revocar mandato</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Esta acción revocará el mandato de representación de {intervinientes[revokeHolderIndex]?.name ?? contract.client_name}.
                  El mandato dejará de tener validez legal y el titular deberá firmar uno nuevo si es necesario.
                </p>
              </div>
            </div>
            <div className="flex flex-col-reverse sm:flex-row gap-2 pt-2">
              <Button
                onClick={() => setShowRevokeConfirm(false)}
                variant="outline"
                className="w-full sm:w-auto min-h-[44px]"
                disabled={isRevoking}
              >
                Cancelar
              </Button>
              <Button
                onClick={() => handleRevokeMandate(revokeHolderIndex)}
                variant="destructive"
                className="w-full sm:w-auto min-h-[44px]"
                disabled={isRevoking}
              >
                {isRevoking ? 'Revocando...' : 'Sí, revocar mandato'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
